import { type FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import StyledDropdown from "src/components/reusable/StyledDropdown";
import {
  SALES_ATTRIBUTION_ALL_FILTER_VALUE,
  type SalesAttributionCampaignOption,
  type SalesAttributionFilterOptions,
  type SalesAttributionFilterState,
  type SalesAttributionOption,
} from "../utils/salesAttributionTypes";

const ALL = SALES_ATTRIBUTION_ALL_FILTER_VALUE;

const withAllOption = (
  options: SalesAttributionOption[],
  allLabel: string
): SalesAttributionOption[] => [{ value: ALL, label: allLabel }, ...options];

interface FilterFieldProps {
  ariaLabel: string;
  value: string;
  options: Array<SalesAttributionOption & { disabled?: boolean }>;
  onChange: (_value: string) => void;
}

const FilterField: FC<FilterFieldProps> = ({ ariaLabel, value, options, onChange }) => {
  const isSet = value !== "";

  return (
    <div
      className={`sales-attribution__filter-field ${isSet ? "sales-attribution__filter-field--set" : ""
        }`}
    >
      <StyledDropdown
        value={isSet ? value : ALL}
        options={options}
        onChange={next => onChange(next === ALL ? "" : String(next))}
        className={`filter-dropdown ${!isSet
            ? "custom-dropdown--all-selected"
            : "custom-dropdown--has-value"
          }`}
        popupClassName="filter-dropdown-menu"
        aria-label={ariaLabel}
        showSearch
        optionFilterProp="label"
      />
      {isSet && <span className="sales-attribution__filter-indicator" aria-hidden="true" />}
    </div>
  );
};

interface SalesAttributionFilterBarProps {
  filters: SalesAttributionFilterState;
  options: SalesAttributionFilterOptions;
  onChange: (_next: SalesAttributionFilterState) => void;
}

export const SalesAttributionFilterBar: FC<SalesAttributionFilterBarProps> = ({
  filters,
  options,
  onChange,
}) => {
  const { t } = useTranslation();
  const allSourcesLabel = t("salesAttribution.ui.filters.allSources", {
    defaultValue: "All Sources",
  });
  const allMediumsLabel = t("salesAttribution.ui.filters.allMediums", {
    defaultValue: "All Mediums",
  });
  const allCampaignsLabel = t("salesAttribution.ui.filters.allCampaigns", {
    defaultValue: "All Campaigns",
  });
  const allPlatformsLabel = t("salesAttribution.ui.filters.allPlatforms", {
    defaultValue: "All Platforms",
  });

  const utmSourceOptions = useMemo(
    () => withAllOption(options.utmSources, allSourcesLabel),
    [options.utmSources, allSourcesLabel]
  );
  const utmMediumOptions = useMemo(
    () => withAllOption(options.utmMediums, allMediumsLabel),
    [options.utmMediums, allMediumsLabel]
  );
  const utmCampaignOptions = useMemo(
    () => withAllOption(options.utmCampaigns, allCampaignsLabel),
    [options.utmCampaigns, allCampaignsLabel]
  );

  const platformOptions = useMemo(
    () =>
      (options.platforms.length > 0
        ? options.platforms
        : [{ value: ALL, label: allPlatformsLabel }]
      ).map(option => ({
        value: option.value,
        label: option.value === ALL ? allPlatformsLabel : option.label,
        disabled: option.available === false,
      })),
    [options.platforms, allPlatformsLabel]
  );

  const campaignOptions = useMemo(() => {
    const matchesPlatform = (option: SalesAttributionCampaignOption) =>
      !filters.platform ||
      filters.platform === ALL ||
      (option.platforms ?? []).includes(filters.platform);

    return withAllOption(
      options.campaigns.filter(matchesPlatform).map(option => ({
        value: option.value,
        label: option.label,
      })),
      allCampaignsLabel
    );
  }, [filters.platform, options.campaigns, allCampaignsLabel]);

  const setField =
    (field: keyof SalesAttributionFilterState) => (value: string) => {
      if (field === "platform") {
        // The chosen campaign may not exist on the new platform.
        onChange({ ...filters, platform: value, campaign: "" });
        return;
      }
      onChange({ ...filters, [field]: value });
    };

  const hasAnyFilterSet =
    filters.utmSource !== "" ||
    filters.utmMedium !== "" ||
    filters.utmCampaign !== "" ||
    filters.platform !== "" ||
    filters.campaign !== "";

  const clearAllFilters = () => {
    onChange({
      utmSource: "",
      utmMedium: "",
      utmCampaign: "",
      platform: "",
      campaign: "",
    });
  };

  return (
    <div className="sales-attribution__filter-bar">
      <FilterField
        ariaLabel={t("salesAttribution.ui.filters.utmSource", { defaultValue: "UTM Source" })}
        value={filters.utmSource}
        options={utmSourceOptions}
        onChange={setField("utmSource")}
      />
      <FilterField
        ariaLabel={t("salesAttribution.ui.filters.utmMedium", { defaultValue: "UTM Medium" })}
        value={filters.utmMedium}
        options={utmMediumOptions}
        onChange={setField("utmMedium")}
      />
      <FilterField
        ariaLabel={t("salesAttribution.ui.filters.utmCampaign", { defaultValue: "UTM Campaign" })}
        value={filters.utmCampaign}
        options={utmCampaignOptions}
        onChange={setField("utmCampaign")}
      />
      <span className="sales-attribution__filter-bar-cell" aria-hidden="true" />
      <div className="sales-attribution__filter-row-secondary">
        <FilterField
          ariaLabel={t("salesAttribution.ui.filters.platform", { defaultValue: "Platform" })}
          value={filters.platform}
          options={platformOptions}
          onChange={setField("platform")}
        />
        <FilterField
          ariaLabel={t("salesAttribution.ui.filters.campaign", { defaultValue: "Campaign" })}
          value={filters.campaign}
          options={campaignOptions}
          onChange={setField("campaign")}
        />
        {hasAnyFilterSet && (
          <button
            type="button"
            className="sales-attribution__filter-bar-clear"
            onClick={clearAllFilters}
          >
            <span className="sales-attribution__filter-bar-clear-icon" aria-hidden="true" />
            <span className="sales-attribution__filter-bar-clear-text">
              {t("salesAttribution.ui.filters.clear", { defaultValue: "Clear Filters" })}
            </span>
          </button>
        )}
      </div>
    </div>
  );
};

export default SalesAttributionFilterBar;
